import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import { createNotification } from "@/lib/createNotification";
import type { Job } from "./JobModal";

export type JobStep = {
  id: string;
  tenant_id: string;
  job_id: string;
  step_key: string;
  title: string;
  description: string | null;
  status: string;
  sort_order: number;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

const STEP_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "blocked", label: "Blocked" },
];

const STEP_STATUS_STYLES: Record<string, string> = {
  pending: "bg-white/8 text-slate-400",
  in_progress: "bg-amber-400/15 text-amber-400",
  completed: "bg-emerald-400/15 text-emerald-400",
  blocked: "bg-red-400/15 text-red-400",
};

const STEP_STATUS_ICONS: Record<string, string> = {
  pending: "ri-time-line",
  in_progress: "ri-loader-4-line",
  completed: "ri-checkbox-circle-fill",
  blocked: "ri-close-circle-line",
};

interface Props {
  job: Job;
  tenantId: string;
  onClose: () => void;
}

export default function WorkflowModal({ job, tenantId, onClose }: Props) {
  const [steps, setSteps] = useState<JobStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [editingDueDate, setEditingDueDate] = useState<string | null>(null);

  const fetchSteps = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("job_steps")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("sort_order", { ascending: true });

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else {
      setSteps((data as JobStep[]) ?? []);
    }
    setLoading(false);
  }, [job.id, tenantId]);

  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  const handleStatusChange = async (step: JobStep, newStatus: string) => {
    setSavingId(step.id);
    const completedAt = newStatus === "completed" ? new Date().toISOString() : null;
    const { data, error } = await supabase
      .from("job_steps")
      .update({
        status: newStatus,
        completed_at: completedAt,
        updated_at: new Date().toISOString(),
      })
      .eq("id", step.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else if (data) {
      setSteps((prev) => prev.map((s) => (s.id === step.id ? (data as JobStep) : s)));
      if (newStatus === 'completed') {
        createNotification({
          tenantId,
          type: 'success',
          title: 'Workflow step completed',
          message: `Step "${step.title}" in job "${job.title}" marked as completed.`,
        });
      }
    }
    setSavingId(null);
  };

  const handleDueDateSave = async (step: JobStep, newDate: string) => {
    setSavingId(step.id);
    const { data, error } = await supabase
      .from("job_steps")
      .update({
        due_date: newDate || null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", step.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else if (data) {
      setSteps((prev) => prev.map((s) => (s.id === step.id ? (data as JobStep) : s)));
    }
    setSavingId(null);
    setEditingDueDate(null);
  };

  const completedCount = steps.filter((s) => s.status === "completed").length;
  const progressPct = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl border border-slate-200 border border-slate-200 rounded-2xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0 flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-4 h-4 flex items-center justify-center text-[#2563eb]">
                <i className="ri-git-branch-line text-sm"></i>
              </span>
              <p className="text-slate-400 text-xs uppercase tracking-wider">Job Workflow</p>
            </div>
            <h2 className="text-[#0f172a] font-semibold text-base truncate">{job.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer shrink-0"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-xs">Progress</span>
            <span className="text-[#2563eb] text-xs font-semibold">{completedCount}/{steps.length} steps · {progressPct}%</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#C8A96E] rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Steps list */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {steps.map((step, idx) => {
                const isEditing = editingDueDate === step.id;
                const isSaving = savingId === step.id;

                return (
                  <div
                    key={step.id}
                    className={`rounded-xl border transition-all ${
                      step.status === "completed"
                        ? "border-emerald-400/20 bg-emerald-400/5"
                        : step.status === "blocked"
                        ? "border-red-400/20 bg-red-400/5"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Step number / icon */}
                        <div
                          className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold shrink-0 mt-0.5 ${
                            STEP_STATUS_STYLES[step.status]
                          }`}
                        >
                          {step.status === "completed" ? (
                            <i className="ri-check-line text-xs"></i>
                          ) : (
                            <span>{idx + 1}</span>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Title row */}
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <p
                              className={`text-sm font-medium ${
                                step.status === "completed" ? "text-slate-400 line-through" : "text-[#0f172a]"
                              }`}
                            >
                              {step.title}
                            </p>
                            {isSaving ? (
                              <div className="w-4 h-4 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin shrink-0"></div>
                            ) : null}
                          </div>

                          {/* Status + due date row */}
                          <div className="flex items-center gap-3 flex-wrap">
                            {/* Status select */}
                            <select
                              value={step.status}
                              onChange={(e) => handleStatusChange(step, e.target.value)}
                              disabled={isSaving}
                              className={`text-xs font-medium px-2.5 py-1 rounded-full border-0 outline-none cursor-pointer transition-colors appearance-none ${
                                STEP_STATUS_STYLES[step.status]
                              } disabled:opacity-50`}
                            >
                              {STEP_STATUSES.map((s) => (
                                <option key={s.value} value={s.value} className="bg-white rounded-xl border border-slate-200 text-[#0f172a]">
                                  {s.label}
                                </option>
                              ))}
                            </select>

                            {/* Status icon */}
                            <span className={`w-3.5 h-3.5 flex items-center justify-center ${STEP_STATUS_STYLES[step.status]}`}>
                              <i className={`${STEP_STATUS_ICONS[step.status]} text-xs`}></i>
                            </span>

                            {/* Due date */}
                            {isEditing ? (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="datetime-local"
                                  defaultValue={step.due_date ? step.due_date.slice(0, 16) : ""}
                                  autoFocus
                                  className="bg-white rounded-xl border border-slate-200 border border-slate-200 rounded-md px-2 py-0.5 text-xs text-[#0f172a] outline-none focus:border-[#2563eb]/50"
                                  onBlur={(e) => handleDueDateSave(step, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleDueDateSave(step, (e.target as HTMLInputElement).value);
                                    } else if (e.key === "Escape") {
                                      setEditingDueDate(null);
                                    }
                                  }}
                                />
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingDueDate(step.id)}
                                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-500 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <span className="w-3.5 h-3.5 flex items-center justify-center">
                                  <i className="ri-calendar-2-line text-xs"></i>
                                </span>
                                {step.due_date
                                  ? new Date(step.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                  : "Set due date"}
                              </button>
                            )}

                            {/* Completed at */}
                            {step.completed_at && (
                              <span className="text-xs text-emerald-400/60 whitespace-nowrap">
                                Done {new Date(step.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {steps.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-10">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-slate-300">
                    <i className="ri-git-branch-line text-xl"></i>
                  </div>
                  <p className="text-slate-400 text-sm">No workflow steps found for this job.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
